import { formatDate } from '@seen/core/dates'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import type { DocumentReviewTask } from '@seen/core/models/tasks/standard/documentReviewTask'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import {
  Badge,
  Button,
  cn,
  Item,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@seen/ui'
import { IconUsers } from '@tabler/icons-react'
import { isBefore, isToday, startOfDay } from 'date-fns'
import { type ReactNode, useMemo } from 'react'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { DateDisplay } from '#src/components/display/DateTimeDisplay.tsx'
import { CategoryBadge } from '#src/components/document-reference/CategoryBadge.tsx'
import { IconTaskState } from '#src/components/icons/IconTaskState.tsx'
import { ParticipantNameplate } from '#src/components/nameplates/ParticipantNameplate.tsx'
import { PractitionerNameplate } from '#src/components/nameplates/PractitionerNameplate.tsx'
import { OrderBadge } from '#src/components/task/OrderBadge.tsx'
import { TaskPriorityCombobox } from '#src/components/task/TaskPriorityCombobox.tsx'
import { TaskPriorityIcon } from '#src/components/task/TaskPriorityIcon.tsx'
import { useRead } from '#src/hooks/useRead.ts'

interface DocumentInboxTaskRowProps {
  task: DocumentReviewTask
  isSelected: boolean
  onSelect: () => void
}

export function DocumentInboxTaskRow({
  task,
  isSelected,
  onSelect,
}: DocumentInboxTaskRowProps): ReactNode {
  const { data: document } = useRead(
    ExtendedDocumentReference,
    task.document as { reference: string; type: 'DocumentReference' }
  )
  const { data: owner } = useRead(ExtendedPractitioner, task.ownerPractitioner)

  const dueLabel = useMemo(() => {
    if (!task.dueOn) {
      return null
    }
    const dueDate = startOfDay(new Date(task.dueOn))
    const today = startOfDay(new Date())

    if (isBefore(dueDate, today)) {
      return 'Overdue'
    }
    if (isToday(dueDate)) {
      return 'Due today'
    }
    return `Due ${formatDate(task.dueOn)}`
  }, [task.dueOn])

  if (!document) {
    return null
  }

  return (
    <Item
      variant="outline"
      size="sm"
      onClick={onSelect}
      className={cn(
        'w-full cursor-pointer rounded-none border-t-0 border-r-0 border-b border-l-4 transition-all',
        isSelected
          ? 'border-l-primary bg-accent'
          : 'border-l-transparent hover:border-l-primary/50 hover:bg-accent/50'
      )}
    >
      <ItemContent className="flex-col gap-2 overflow-hidden">
        <div className="flex w-full items-center justify-between gap-3">
          <ItemTitle className="flex items-center gap-1.5 truncate text-sm leading-tight text-foreground">
            <ResourceFieldForm
              instance={task}
              valueFn={(t) => t.priority ?? null}
              patchFn={(t, value) => {
                t.priority = value ?? undefined
              }}
              className="shrink-0"
              render={({ field: { value, onChange, ...rest } }) => (
                <TaskPriorityCombobox onSelect={onChange} selected={value}>
                  <Button
                    variant="unstyled"
                    className="h-auto p-0 hover:brightness-hover-icon data-[state=open]:brightness-hover-icon"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    {...rest}
                  >
                    <TaskPriorityIcon
                      priority={value ?? undefined}
                      isTaskCompletedOrCancelled={task.isCompletedOrCancelled}
                      className="shrink-0"
                      tooltip={
                        StandardTask.priorityText(value ?? undefined) ??
                        'No priority'
                      }
                    />
                  </Button>
                </TaskPriorityCombobox>
              )}
            />
            {task.state && (
              <IconTaskState state={task.state} className="shrink-0" />
            )}
            <span className="truncate">
              {document.description || document.title || 'Untitled Document'}
            </span>
          </ItemTitle>
          <DateDisplay
            value={document.date}
            format="MM/dd/yyyy h:mma"
            element="span"
            className="shrink-0 text-xs text-muted-foreground"
          />
        </div>

        {document.title && document.description !== document.title && (
          <ItemDescription className="max-w-full truncate text-xs text-nowrap whitespace-nowrap">
            {document.title}
          </ItemDescription>
        )}

        <div className="flex items-end justify-between gap-2">
          <div className="flex items-center gap-2">
            {document.hasMultiplePredictedSubjects && (
              <Badge
                variant="outline"
                color="yellow"
                className="flex items-center gap-1"
              >
                <IconUsers className="size-3" />
                Multi-patient
              </Badge>
            )}
            {document.sourceDisplay && (
              <Badge variant="outline">{document.sourceDisplay}</Badge>
            )}
            {task.participant && (
              <Badge variant="outline" className="flex items-center gap-1.5">
                <ParticipantNameplate participant={task.participant} />
              </Badge>
            )}
            {dueLabel && (
              <Badge
                variant="outline"
                color={dueLabel === 'Overdue' ? 'red' : undefined}
                className="text-xs"
              >
                {dueLabel}
              </Badge>
            )}
            {document.orders && document.orders.length > 0 && (
              <OrderBadge order={document.orders[0]} />
            )}
            {document.categories.length > 0 && (
              <div className="flex gap-1">
                {document.categories.map((category) => (
                  <CategoryBadge key={category} category={category} />
                ))}
              </div>
            )}
          </div>
          {owner && (
            <div className="shrink-0">
              <PractitionerNameplate practitioner={owner.reference} />
            </div>
          )}
        </div>
      </ItemContent>
    </Item>
  )
}
